// v3.21-1612 · Minimal Ghost follow-up completion dialog.
//
// Replaces the retired CallOutcomeModal for the Ghost Daily Organizer
// follow-up-loop only (POST /call-follow-ups/{id}/complete).
//
// A live client call never opens this — ActiveCallPill's Dispose +
// Notes + End Call is the SSOT for live calls.  This dialog exists
// solely to close out a scheduled follow-up TASK that was created
// against a client but hasn't produced a Twilio call session.
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

const DISPOSITIONS = [
  ['no_answer',            'No Answer'],
  ['left_voicemail',       'Voicemail Left'],
  ['spoke_not_interested', 'Spoke - Not Interested'],
  ['spoke_interested',     'Spoke - Interested'],
  ['touched_base',         'Touched Base'],
  ['call_back',            'Call Back'],
];

export default function FollowUpCompleteDialog({ open, data, onComplete }) {
  const [disp, setDisp] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setDisp(''); setNotes(''); setSaving(false); }
  }, [open, data?.followUpId]);

  const submit = async () => {
    if (!disp) { toast.error('Pick a disposition'); return; }
    if (!notes.trim() || notes.trim().length < 3) { toast.error('Notes must be at least 3 characters.'); return; }
    if (!data?.followUpId) { toast.error('Missing follow-up id'); return; }
    setSaving(true);
    try {
      await api.post(`/call-follow-ups/${data.followUpId}/complete`, {
        outcome:         disp,
        notes:           notes.trim(),
        call_direction:  data.direction || 'outbound',
        phone_number:    data.phone || null,
        call_log_id:     data.callLogId || null,
      });
      toast.success('Follow-up completed');
      onComplete?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to complete follow-up');
    }
    setSaving(false);
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onComplete?.()}>
      <DialogContent className="max-w-md" data-testid="follow-up-complete-dialog">
        <DialogHeader>
          <DialogTitle>Complete follow-up{data?.clientName ? ` · ${data.clientName}` : ''}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-slate-700">Disposition</label>
            <select className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                    value={disp}
                    onChange={(e) => setDisp(e.target.value)}
                    data-testid="follow-up-disposition">
              <option value="">— Choose —</option>
              {DISPOSITIONS.map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Notes (required)</label>
            <Textarea rows={3} value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      data-testid="follow-up-notes"
                      placeholder="What happened on this follow-up?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onComplete?.()}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !disp || !notes.trim()}
                  data-testid="follow-up-complete-submit">
            {saving ? 'Saving…' : 'Complete follow-up'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
